/* eslint-disable camelcase */
import { apiContenedoresImportacionGet } from "../../api/descargas";
import { apiListaMaterialesPorFechaLimiteGet } from "../../api/materiales/listaMateriales";
import { homologaNumeroPedimento } from "../../common/pedimentos/utils";
import { dateFilterImportacionSelectChoices } from "./choices";
import { dataTablePedimentos } from "./dataTables";
import {
    alertPedimentosIncompletos,
    alertSinFechaVencimiento,
    alertSinPedimentoSeleccionado,
    alertSinPedimentosParaDescargar,
} from "./descarga.alerts";
import { TipoOperacionPedimentoEnum } from "../../common/catalogos";
import { alertGenericError } from "../../common/alerts/alertGenericError";
import { cancelarDescargaConfirmed } from "./descarga.finalizar";

/**
 * Verifica y genera una alerta en caso de que existan pedimentos
 * de exportación incompletos
 * @param {Array} contenedoresExportacion
 * @returns
 */
export const verificaPedimentosIncompletos = (contenedoresExportacion = []) => {
    const pedimentosNoValidos = contenedoresExportacion
        .filter(({ material }) => material === null || material === "")
        .reduce((previousValue, currentValue) => {
            const existe = previousValue.findIndex((itm) => itm.pedimento_id === currentValue.pedimento_id);

            if (existe > -1) return previousValue;

            const { pedimento_id, fecha_pago_real, seccion, patente, pedimento } = currentValue;

            previousValue.push({
                pedimento_id,
                pedimento_formato: homologaNumeroPedimento({
                    fechaPagoReal: fecha_pago_real,
                    seccion,
                    patente,
                    pedimento,
                }),
            });

            return previousValue;
        }, []);

    if (pedimentosNoValidos.length > 0) {
        const listaPedimentosNoValidos = pedimentosNoValidos
            .map((pedimento) => pedimento.pedimento_formato)
            .join("<br>");

        alertPedimentosIncompletos(listaPedimentosNoValidos);
    }

    return pedimentosNoValidos;
};

/**
 * Comprueba y alerta en caso de no tener pedimentos de exportación
 * @param {number} totalPedimentosExportacion
 * @returns
 */
export const isEmptyBusquedaPedimentos = (totalPedimentosExportacion) => {
    if (totalPedimentosExportacion === 0) {
        alertSinPedimentosParaDescargar();

        return true;
    }
    return false;
};

/**
 * Valida si el usuario selecciono algún pedimento de expo para descargar
 * @returns true si existen pedimentos, false en caso contrario
 */
export const isPedimentosExportacionSeleccionados = () => {
    const itemSelectedIndex = dataTablePedimentos.options.data.findIndex((itm) => itm["item-selected"] !== undefined);

    const existenPedimentos = itemSelectedIndex > -1;

    if (!existenPedimentos) {
        alertSinPedimentoSeleccionado();
    }

    return existenPedimentos;
};

/**
 * Genera una alerta en caso de tener pedimentos de importación
 * donde no se pudo calcular la fecha de vencimiento
 * @param {Array} sinFechaVencimiento
 * @returns
 */
export const verificaSinFechaVencimiento = (sinFechaVencimiento = []) => {
    if (sinFechaVencimiento.length === 0) return;

    const data = sinFechaVencimiento.join("<br/>");

    alertSinFechaVencimiento(data);
};

/**
 * @typedef {Object} PedimentosExportacionValidos
 * @property {number} pedimento_id
 * @property {string} pedimento_formato - numero de pedimento formateado
 * @property {string} fecha - formato 'YYYY-MM-DD'
 * @property {string} clave_documento
 * @property {Array} detalles - El contenedor a detalle
 */

/**
 * Genera un objeto con los pedimentos de exportación y su detalle
 * @param {Array} contenedores
 * @returns {PedimentosExportacionValidos}
 */
export const buildPedimentosExportacionValidos = (contenedores = []) =>
    // eslint-disable-next-line implicit-arrow-linebreak
    contenedores.reduce((previousValue, currentValue) => {
        const existe = previousValue[currentValue.pedimento_id] !== undefined;

        if (!existe) {
            let pedimentoFormato = [currentValue.pedimento, "-", currentValue.pedimento_id].join("");

            if (
                currentValue.tipo_operacion === TipoOperacionPedimentoEnum.Importacion ||
                currentValue.tipo_operacion === TipoOperacionPedimentoEnum.Exportacion
            ) {
                pedimentoFormato = homologaNumeroPedimento({
                    fechaPagoReal: currentValue.fecha,
                    seccion: currentValue.seccion,
                    patente: currentValue.patente,
                    pedimento: currentValue.pedimento,
                });
            }

            previousValue[currentValue.pedimento_id] = {
                pedimento_id: currentValue.pedimento_id,
                pedimento_formato: `${pedimentoFormato}-${currentValue.pedimento_id}`,
                fecha: currentValue.fecha,
                clave_documento: currentValue.clave_documento,
                detalle: [],
            };
        }

        previousValue[currentValue.pedimento_id].detalle.push(currentValue);

        return previousValue;
    }, {});

/**
 * Retorna los contenedores de importacion dependiendo de los materiales
 * @param {Array} materiales Tupla de numeros de parte
 * @returns respuesta del API
 */
export const getContenedoresImportacion = async (materiales = []) => {
    const { value: filtroImportacion } = dateFilterImportacionSelectChoices.getValue();

    try {
        return await apiContenedoresImportacionGet(materiales, filtroImportacion);
    } catch (error) {
        alertGenericError().then(cancelarDescargaConfirmed());
        return {
            simulacion_descarga: [],
            sin_fecha_vencimiento: [],
        };
    }
};

/**
 * Obtiene la lista de materiales del material donde la fecha de inicio de la lista
 * sea anterior a la fecha limite
 * @param {string} material material padre
 * @param {Date} fecha fecha limite para la lista de materiales
 * @returns
 */
export const getListaMateriales = async (material, fecha) => {
    const listaMateriales = await apiListaMaterialesPorFechaLimiteGet(material, fecha);

    if (!listaMateriales) {
        return null;
    }

    return listaMateriales;
};

/**
 * Retorna el pedimento para descargar basado en el item seleccionado de la
 * dataTablePedimentos
 * @returns
 */
export const getPedimentoParaDescargar = () => {
    const itemSelectedIndex = dataTablePedimentos.options.data.findIndex((itm) => itm["item-selected"] !== undefined);
    return dataTablePedimentos.options.data[itemSelectedIndex];
};
